import React, { useCallback, useState } from 'react';

import {
  SearchParamsPetCounts,
  SearchParamsPetType,
  SITTER_PROFILE_EVENTSTREAM_EVENTS,
} from '@rover/constants/src/translations/sitterProfilePage.constants';
import { Box } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { PriceTransparencyLevel } from '@rover/types/src/PriceTransparencyLevel';
import emitMetric from '@rover/utilities/emitMetric';
import fireDataLayerEvent from '@rover/utilities/fireDataLayerEvent';

import { ViewerOptions } from '../../constants';
import { useRevampProfileContext } from '../../hooks/useRevampProfileContext';

import { SitterProfileServiceDropdownChanged } from './analyticsEvents';
import RevampServicesCtaLoadingShimmer from './RevampServicesCtaLoadingShimmer';
import RevampServicesCtaOwnerView from './RevampServicesCtaOwnerView';
import RevampServicesCtaSelfView from './RevampServicesCtaSelfView';
import RevampServiceSelector from './RevampServiceSelector';

export type CTAProps = {
  priceTransparencyLevel: PriceTransparencyLevel;
  language: string;
  petType: SearchParamsPetType;
  petCount: SearchParamsPetCounts;
  bookingFeePercentage: string;
};

const RevampServicesCta = ({
  priceTransparencyLevel,
  language,
  petType,
  petCount,
  bookingFeePercentage,
}: CTAProps): JSX.Element => {
  const { sitterData, serviceType, setServiceType, viewMode } = useRevampProfileContext();

  const theme = useTheme();

  const hasServices = sitterData.services.length > 0;
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const handleOpenSheet = useCallback(() => {
    setIsSheetOpen(true);
  }, []);

  const handleCloseSheet = useCallback(() => {
    setIsSheetOpen(false);
  }, []);

  const handleServiceSave = useCallback(
    (slug: string) => {
      if (slug !== serviceType) {
        setServiceType(slug);
        fireDataLayerEvent({
          event: SITTER_PROFILE_EVENTSTREAM_EVENTS.CHANGE_SERVICE,
          serviceType: slug,
          providerId: sitterData.opk,
        });
        emitMetric('sitter_profile_service_changed', { component: 'revamp-cta' });
        emitAnalyticsEvent(
          new SitterProfileServiceDropdownChanged({
            serviceSlug: slug,
            providerId: sitterData.opk,
            pageVariant: 'revamp',
          })
        );
      }
    },
    [serviceType, setServiceType, sitterData.opk]
  );

  return (
    <>
      <Box
        data-testid="revamp-services-cta"
        px="4x"
        py="8x"
        borderRadius="16px"
        backgroundColor={theme.background.primary}
        sx={{
          boxShadow: '0px 2px 12px 0px rgba(27, 31, 35, 0.24)',
        }}
      >
        {(() => {
          if (!hasServices) return <RevampServicesCtaLoadingShimmer />;
          if (viewMode.viewer === ViewerOptions.SELF)
            return (
              <RevampServicesCtaSelfView
                priceTransparencyLevel={priceTransparencyLevel}
                language={language}
                petCount={petCount}
                petType={petType}
                bookingFeePercentage={bookingFeePercentage}
                handleOpenSheet={handleOpenSheet}
                isOpen={isSheetOpen}
              />
            );
          return (
            <RevampServicesCtaOwnerView
              priceTransparencyLevel={priceTransparencyLevel}
              language={language}
              petCount={petCount}
              petType={petType}
              bookingFeePercentage={bookingFeePercentage}
              handleOpenSheet={handleOpenSheet}
              isOpen={isSheetOpen}
            />
          );
        })()}
      </Box>
      <RevampServiceSelector
        isOpen={isSheetOpen}
        onClose={handleCloseSheet}
        services={sitterData.services}
        selectedServiceSlug={serviceType}
        onSave={handleServiceSave}
        variant="desktop"
      />
    </>
  );
};

export default RevampServicesCta;
